import {
  deleteOAuthConnectionResponseSchema,
  listOAuthConnectionsResponseSchema,
  mcpAccessStatusResponseSchema,
  notFoundResponseSchema,
  oauthAuthorizationViewResponseSchema,
  oauthDecisionRequestSchema,
  oauthDecisionSuccessResponseSchema,
  permissionDeniedResponseSchema,
} from 'xosign-types';
import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Extend request objects only for the frontend with necessary path params
const CRUD = {
  r: z.object({ authorizationId: z.string() }),
  decide: oauthDecisionRequestSchema.extend({ authorizationId: z.string() }),
  d: z.object({ connectionId: z.string() }),
};

const endpoints = {
  'oauth.authorization.get': {
    method: 'GET',
    url: `${API_URL_BASE}oauth/authorizations/:authorizationId`,
    requestSchema: CRUD.r,
    responseSuccessSchema: oauthAuthorizationViewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof oauthAuthorizationViewResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'oauth.authorization.decide': {
    method: 'POST',
    url: `${API_URL_BASE}oauth/authorizations/:authorizationId/decision`,
    requestSchema: CRUD.decide,
    responseSuccessSchema: oauthDecisionSuccessResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.decide,
    typeof oauthDecisionSuccessResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'oauth.connections.get.all': {
    method: 'GET',
    url: `${API_URL_BASE}oauth/connections`,
    requestSchema: z.undefined(),
    responseSuccessSchema: listOAuthConnectionsResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof listOAuthConnectionsResponseSchema,
    z.ZodUndefined
  >,
  'oauth.connections.delete': {
    method: 'DELETE',
    url: `${API_URL_BASE}oauth/connections/:connectionId`,
    requestSchema: CRUD.d,
    responseSuccessSchema: deleteOAuthConnectionResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.d,
    typeof deleteOAuthConnectionResponseSchema,
    {
      404: typeof notFoundResponseSchema;
    }
  >,
  'mcp.access.get': {
    method: 'GET',
    url: `${API_URL_BASE}mcp/access`,
    requestSchema: z.undefined(),
    responseSuccessSchema: mcpAccessStatusResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof mcpAccessStatusResponseSchema,
    z.ZodUndefined
  >,
};

export default endpoints;
